import { TermsOfServiceStructuredData } from '@/components/seo/TermsOfServiceStructuredData';
import { LegalLayout, type LegalSection } from '@/components/legal/LegalLayout';
import { useSEO } from '@/hooks/useSEO';

const SECTIONS: LegalSection[] = [
  { id: 'agreement', label: 'Agreement to Terms' },
  { id: 'description', label: 'Description of Service' },
  { id: 'account', label: 'Account Registration and Security' },
  { id: 'acceptable-use', label: 'Acceptable Use Policy' },
  { id: 'intellectual-property', label: 'Intellectual Property Rights' },
  { id: 'user-content', label: 'User Content and Data' },
  { id: 'ai-disclaimer', label: 'AI-Generated Analysis Disclaimer' },
  { id: 'integrations', label: 'Third-Party Integrations' },
  { id: 'payment', label: 'Payment Terms' },
  { id: 'liability', label: 'Limitation of Liability' },
  { id: 'indemnification', label: 'Indemnification' },
  { id: 'termination', label: 'Termination' },
  { id: 'disputes', label: 'Dispute Resolution' },
  { id: 'governing-law', label: 'Governing Law' },
  { id: 'changes', label: 'Changes to Terms' },
  { id: 'contact', label: 'Contact Information' },
];

export default function TermsOfService() {
  useSEO({
    title: 'Terms of Service | Beatview',
    description: 'Review the terms and conditions for using Beatview\'s AI-powered recruitment platform. Understand your rights and responsibilities as a user.',
    canonicalUrl: 'https://beatview.ai/terms-of-service',
  });

  return (
    <LegalLayout
      title={<>Terms of <span className="bv-hero-highlight">Service</span></>}
      updated="September 19, 2026"
      summary="The agreement that governs your use of Beatview: accounts, acceptable use, AI-generated analysis, integrations and AI assistant connections, payment, liability and disputes."
      current="/terms-of-service"
      sections={SECTIONS}
      contact={{ label: 'Questions about these terms?', email: 'legal@beatview.ai' }}
    >
      <TermsOfServiceStructuredData />
          <section id="agreement">
            <h2>1. Agreement to Terms</h2>
            <p>
              By accessing or using Beatview's AI-powered talent intelligence platform ("Service"), you 
              agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of 
              these terms, you may not access the Service.
            </p>
            <p>
              These Terms apply to all visitors, users, and others who access or use the Service, including 
              employers, recruiters, hiring managers, and job candidates.
            </p>
          </section>
          <section id="description">
            <h2>2. Description of Service</h2>
            <p>
              Beatview provides an AI-powered recruitment platform that includes:
            </p>
            <ul>
              <li><strong>Resume Parsing & Analysis:</strong> Automated extraction and AI-powered analysis of candidate resumes</li>
              <li><strong>AI Video Interviews:</strong> Asynchronous video interview recording with AI-generated scoring and feedback</li>
              <li><strong>Work Style Assessments:</strong> Personality and work style evaluations for cultural fit analysis</li>
              <li><strong>Pipeline Management:</strong> Candidate tracking through customizable hiring stages</li>
              <li><strong>Career Page Hosting:</strong> Branded career pages for job listings and applications</li>
              <li><strong>Interview Preparation:</strong> AI-generated prep reports for hiring managers</li>
              <li><strong>Candidate Sharing:</strong> Secure sharing of candidate profiles with stakeholders</li>
              <li><strong>Integrations:</strong> Connections with third-party services like LinkedIn and Zapier</li>
              <li><strong>AI Assistant Connections (MCP Server):</strong> Connections that let AI assistants and agents you choose, such as Claude, ChatGPT and Gemini, work with your account</li>
            </ul>
          </section>
          <section id="account">
            <h2>3. Account Registration and Security</h2>
            <h3>3.1 Account Creation</h3>
            <p>
              To access certain features, you must register for an account. You agree to provide accurate, 
              current, and complete information during registration and to update such information to 
              keep it accurate.
            </p>

            <h3>3.2 Account Security</h3>
            <p>
              You are responsible for safeguarding your account credentials and for any activities or 
              actions under your account. You must immediately notify us of any unauthorized use of your 
              account.
            </p>

            <h3>3.3 Account Requirements</h3>
            <ul>
              <li>You must be at least 18 years old to create an account</li>
              <li>You must have the authority to bind your organization if creating a business account</li>
              <li>You may not share your account credentials with unauthorized users</li>
            </ul>
          </section>
          <section id="acceptable-use">
            <h2>4. Acceptable Use Policy</h2>
            <p>You agree not to use the Service to:</p>
            <ul>
              <li>Violate any applicable laws, regulations, or third-party rights</li>
              <li>Discriminate against candidates based on protected characteristics</li>
              <li>Submit false, misleading, or fraudulent information</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Scrape, harvest, or collect data without authorization</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Use the Service for any purpose other than lawful recruitment activities</li>
              <li>Misrepresent job opportunities or employer information</li>
              <li>Contact candidates for purposes unrelated to the posted position</li>
              <li>Use a connected AI assistant, agent or the MCP server to bulk-export candidate data, get around plan or rate limits, or access data outside your own account</li>
            </ul>
          </section>
          <section id="intellectual-property">
            <h2>5. Intellectual Property Rights</h2>
            <h3>5.1 Our Intellectual Property</h3>
            <p>
              The Service, including its original content, features, functionality, and underlying technology, 
              is owned by Beatview and protected by international copyright, trademark, patent, trade secret, 
              and other intellectual property laws.
            </p>

            <h3>5.2 License to Use</h3>
            <p>
              We grant you a limited, non-exclusive, non-transferable, revocable license to use the Service 
              for its intended purposes in accordance with these Terms.
            </p>

            <h3>5.3 Restrictions</h3>
            <p>You may not:</p>
            <ul>
              <li>Copy, modify, or distribute our proprietary content</li>
              <li>Reverse engineer or decompile the Service</li>
              <li>Remove any copyright or proprietary notices</li>
              <li>Use our trademarks without written permission</li>
            </ul>
          </section>
          <section id="user-content">
            <h2>6. User Content and Data</h2>
            <h3>6.1 Your Content</h3>
            <p>
              You retain ownership of all content you submit to the Service, including job postings, 
              company information, and brand assets. By submitting content, you grant us a license to 
              use, store, and display such content as necessary to provide the Service.
            </p>

            <h3>6.2 Candidate Data</h3>
            <p>
              As an employer, you are responsible for ensuring you have appropriate consent to collect 
              and process candidate data through our platform. You must comply with all applicable 
              privacy and employment laws.
            </p>

            <h3>6.3 Data Processing</h3>
            <p>
              We process data on your behalf as a data processor. Our data processing practices are 
              described in our Privacy Policy and any applicable Data Processing Agreement.
            </p>
          </section>
          <section id="ai-disclaimer">
            <h2>7. AI-Generated Analysis Disclaimer</h2>
            <p>
              <strong>Important:</strong> The AI-powered features of our platform are designed to assist, 
              not replace, human decision-making in the hiring process.
            </p>

            <h3>7.1 Resume Analysis</h3>
            <p>
              AI-generated resume scores and insights are based on automated text analysis and may not 
              capture all relevant candidate qualifications. Always review the original resume.
            </p>

            <h3>7.2 Interview Scoring</h3>
            <p>
              AI interview scores are generated based on linguistic analysis and should be considered 
              as one factor among many. They do not constitute a definitive assessment of a candidate's 
              suitability.
            </p>

            <h3>7.3 Work Style Assessments</h3>
            <p>
              Personality and work style results are based on self-reported responses and statistical 
              models. They should not be used as the sole basis for hiring decisions.
            </p>

            <h3>7.4 Human Oversight Required</h3>
            <p>
              You are solely responsible for all hiring decisions. AI-generated insights are advisory 
              tools and should always be supplemented with human judgment, interviews, and reference checks.
            </p>

            <h3>7.5 Customer Compliance Responsibility</h3>
            <p>
              Customers remain fully responsible for compliance with applicable employment, labor, and 
              anti-discrimination laws in their jurisdiction. Beatview does not make employment decisions 
              on behalf of customers. Our platform provides decision-support tools designed to assist your 
              hiring process—it is not intended to serve as an automated decision-making system.
            </p>
            <p>
              You are responsible for ensuring that your use of our AI-powered features complies with all 
              applicable laws, including but not limited to the EU AI Act, NYC Local Law 144, and similar 
              regulations governing automated employment decision tools.
            </p>

            <h3>7.6 Fair Hiring & Bias Limitations</h3>
            <p>
              Beatview is designed to support fair and structured candidate evaluation. However, AI results 
              may contain limitations and should always be reviewed by qualified humans before any hiring 
              decision is made. Customers are solely responsible for how AI outputs are used in their 
              hiring decisions.
            </p>
            <p>
              <strong>Important:</strong> AI-generated scores, rankings, and insights should never be the 
              sole basis for employment decisions. All hiring decisions must involve meaningful human review 
              and consideration of factors beyond automated assessments.
            </p>
          </section>
          <section id="integrations">
            <h2>8. Third-Party Integrations</h2>
            <p>
              The Service may integrate with third-party services such as:
            </p>
            <ul>
              <li>LinkedIn (for candidate sourcing and profile enrichment)</li>
              <li>Zapier (for workflow automation)</li>
              <li>Email services (for candidate communications)</li>
              <li>Custom webhooks (for data synchronization)</li>
            </ul>
            <p>
              Your use of third-party integrations is subject to those services' terms and privacy policies. 
              We are not responsible for the content, privacy practices, or performance of third-party services.
            </p>

            <h3>8.1 AI Assistants and Agents (MCP Server)</h3>
            <p>
              You may connect AI assistants and agents to your account through the Beatview MCP server. By 
              authorizing a connection, you:
            </p>
            <ul>
              <li>Authorize Beatview to share your account data, including candidate personal data, with that assistant as it requests</li>
              <li>Are responsible for the actions the assistant takes through the connection, including creating, updating or publishing jobs, moving candidates between stages and sending interview invitations, as if you had taken them yourself</li>
              <li>Agree to review AI-generated output and the underlying candidate evidence before making decisions, as described in Section 7.4, and not to use a connected assistant to make hiring decisions without human review</li>
              <li>Confirm that the assistant's provider is approved by your organization for candidate data and that your use complies with applicable law</li>
            </ul>
            <p>
              Connected assistants are third-party services governed by their providers' terms. We are not 
              responsible for how they process data or for errors in their output, including misstatements of 
              information retrieved from Beatview. Plan limits apply to actions taken through a connection. We 
              may limit, suspend or revoke connections, including through rate limits, to protect the Service, 
              candidates or other customers.
            </p>
          </section>
          <section id="payment">
            <h2>9. Payment Terms</h2>
            <h3>9.1 Subscription Plans</h3>
            <p>
              Certain features require a paid subscription. Plan details, pricing, and features are 
              described on our pricing page and in your subscription agreement.
            </p>

            <h3>9.2 Billing</h3>
            <ul>
              <li>Subscriptions are billed in advance on a monthly or annual basis</li>
              <li>All fees are non-refundable unless otherwise specified</li>
              <li>You are responsible for all applicable taxes</li>
              <li>We may change pricing with 30 days' notice</li>
            </ul>

            <h3>9.3 Enterprise Plans</h3>
            <p>
              Enterprise customers may have custom terms outlined in a separate agreement, which takes 
              precedence over these Terms for those specific provisions.
            </p>
          </section>
          <section id="liability">
            <h2>10. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, BEATVIEW SHALL NOT BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul>
              <li>Loss of profits, data, or business opportunities</li>
              <li>Hiring decisions made based on AI-generated insights</li>
              <li>Damages arising from third-party integrations or AI assistants connected to your account</li>
              <li>Service interruptions or data loss</li>
            </ul>
            <p>
              Our total liability shall not exceed the amount you paid us in the twelve (12) months 
              preceding the claim.
            </p>
          </section>
          <section id="indemnification">
            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Beatview and its officers, directors, employees, 
              and agents from any claims, damages, losses, or expenses (including reasonable attorneys' 
              fees) arising from:
            </p>
            <ul>
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Your hiring decisions and employment practices</li>
              <li>Content you submit to the Service</li>
            </ul>
          </section>
          <section id="termination">
            <h2>12. Termination</h2>
            <h3>12.1 Termination by You</h3>
            <p>
              You may terminate your account at any time by contacting us or using the account settings. 
              Upon termination, your access to the Service will be revoked.
            </p>

            <h3>12.2 Termination by Us</h3>
            <p>
              We may suspend or terminate your account immediately if you violate these Terms or for 
              any other reason at our sole discretion.
            </p>

            <h3>12.3 Effect of Termination</h3>
            <p>
              Upon termination, your right to use the Service ceases immediately. We may retain certain 
              data as required by law or for legitimate business purposes.
            </p>
          </section>
          <section id="disputes">
            <h2>13. Dispute Resolution</h2>
            <p>
              Any disputes arising from these Terms or your use of the Service shall be resolved through:
            </p>
            <ol>
              <li><strong>Informal Resolution:</strong> Contact us first to attempt to resolve the dispute informally</li>
              <li><strong>Mediation:</strong> If informal resolution fails, the parties agree to attempt mediation</li>
              <li><strong>Arbitration:</strong> Binding arbitration in accordance with applicable arbitration rules</li>
            </ol>
            <p>
              Class action lawsuits and class-wide arbitration are not permitted under these Terms.
            </p>
          </section>
          <section id="governing-law">
            <h2>14. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction 
              in which Beatview is incorporated, without regard to its conflict of law provisions.
            </p>
          </section>
          <section id="changes">
            <h2>15. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of material 
              changes by posting the new Terms on this page and updating the "Last updated" date. 
              Your continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>
          <section id="contact">
            <h2>16. Contact Information</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <ul>
              <li><strong>Email:</strong> legal@beatview.ai</li>
              <li><strong>Support:</strong> support@beatview.ai</li>
            </ul>
          </section>
    </LegalLayout>
  );
}
